import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { VoiceUiPhase } from '../../hooks/useVoiceUiPhase';

export interface VoiceStatusAnnouncerProps {
  /** Polite status phase for capture/command lifecycle. */
  phase: VoiceUiPhase;
  /** Silence countdown remaining seconds (only while waiting-for-finish). */
  silenceSecondsRemaining?: number | null;
  /** Actionable failure message — announced with role="alert" only when set. */
  alertMessage?: string | null;
}

const PHASE_I18N_KEY: Record<Exclude<VoiceUiPhase, 'idle'>, string> = {
  'requesting-permission': 'voice.status.requestingPermission',
  listening: 'voice.status.listening',
  'speech-detected': 'voice.status.speechDetected',
  'waiting-for-finish': 'voice.status.waitingForFinish',
  finishing: 'voice.status.finishing',
  transcribing: 'voice.status.transcribing',
  understanding: 'voice.status.understanding',
  'confirmation-required': 'voice.status.confirmationRequired',
  completed: 'voice.status.completed',
};

const PHASE_FALLBACK: Record<Exclude<VoiceUiPhase, 'idle'>, string> = {
  'requesting-permission': 'Requesting microphone permission',
  listening: 'Listening',
  'speech-detected': 'Speech detected',
  'waiting-for-finish': 'Waiting to finish',
  finishing: 'Finishing capture',
  transcribing: 'Transcribing',
  understanding: 'Understanding your request',
  'confirmation-required': 'Confirmation required',
  completed: 'Completed',
};

/**
 * Screen-reader announcements for the unified voice state machine.
 * Uses aria-live="polite" for status; role="alert" only for actionable failures.
 */
const VoiceStatusAnnouncer: React.FC<VoiceStatusAnnouncerProps> = ({
  phase,
  silenceSecondsRemaining = null,
  alertMessage = null,
}) => {
  const { t } = useTranslation();
  const lastPoliteRef = useRef('');

  let politeText = '';
  if (phase !== 'idle') {
    politeText = t(PHASE_I18N_KEY[phase], PHASE_FALLBACK[phase]);
    if (
      phase === 'waiting-for-finish' &&
      silenceSecondsRemaining != null &&
      silenceSecondsRemaining > 0
    ) {
      politeText = t('voice.status.silenceCountdown', 'Sending in {{seconds}}s…', {
        seconds: silenceSecondsRemaining,
      });
    }
  }

  useEffect(() => {
    if (politeText && politeText !== lastPoliteRef.current) {
      lastPoliteRef.current = politeText;
    }
  }, [politeText]);

  return (
    <>
      <div
        className="voice-status-announcer"
        aria-live="polite"
        aria-atomic="true"
        data-testid="voice-status-live"
      >
        {politeText}
      </div>
      {alertMessage ? (
        <div
          className="voice-status-announcer"
          role="alert"
          aria-live="assertive"
          data-testid="voice-status-alert"
        >
          {alertMessage}
        </div>
      ) : null}
    </>
  );
};

export default VoiceStatusAnnouncer;
